/**
 * Shop Page - Public storefront for purchasing products
 * Features: Product browsing, cart, checkout with Stripe
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ShoppingCart, ShoppingBag, Search, Filter, X, Plus, Minus, Trash2,
  User, LogIn, LogOut, Package, Truck, MapPin, CreditCard, Heart,
  ChevronLeft, ChevronRight, Loader2, CheckCircle, ArrowRight,
  Home, Grid, List, History, Settings
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// Helper function to safely extract error message from API responses
const getErrorMessage = (error, defaultMsg = 'An error occurred') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return defaultMsg;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (typeof e === 'string' ? e : e?.msg || e?.message || 'Error')).join(', ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || defaultMsg;
  }
  return defaultMsg;
};

// Card element styles
const cardElementStyle = {
  base: {
    color: '#fff',
    fontFamily: '"Inter", sans-serif',
    fontSmoothing: 'antialiased',
    fontSize: '16px',
    '::placeholder': {
      color: '#6b7280'
    }
  },
  invalid: {
    color: '#ef4444',
    iconColor: '#ef4444'
  }
};

// Cart context stored in localStorage
const getCart = () => {
  try {
    const cart = JSON.parse(localStorage.getItem('shop_cart') || '[]');
    // Filter out any invalid items (must have product_id)
    return cart.filter(item => item && item.product_id);
  } catch {
    return [];
  }
};

const saveCart = (cart) => {
  localStorage.setItem('shop_cart', JSON.stringify(cart));
};

// Stripe Payment Form Component
const CheckoutPaymentForm = ({ clientSecret, onSuccess, onError, processing, setProcessing }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [zip, setZip] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
          billing_details: {
            address: { postal_code: zip }
          }
        }
      });

      if (error) {
        onError(error.message);
      } else if (paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent.id);
      }
    } catch (err) {
      onError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-slate-300">Card Number</Label>
        <div className="mt-1 p-3 bg-slate-800 border border-slate-600 rounded-md">
          <CardNumberElement options={{ style: cardElementStyle }} />
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-slate-300">Expiry</Label>
          <div className="mt-1 p-3 bg-slate-800 border border-slate-600 rounded-md">
            <CardExpiryElement options={{ style: cardElementStyle }} />
          </div>
        </div>
        <div>
          <Label className="text-slate-300">CVC</Label>
          <div className="mt-1 p-3 bg-slate-800 border border-slate-600 rounded-md">
            <CardCvcElement options={{ style: cardElementStyle }} />
          </div>
        </div>
        <div>
          <Label className="text-slate-300">ZIP Code</Label>
          <Input
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            placeholder="12345"
            className="mt-1 bg-slate-800 border-slate-600 text-white"
          />
        </div>
      </div>
      
      <Button 
        type="submit" 
        disabled={!stripe || processing}
        className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3"
      >
        {processing ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="h-4 w-4 mr-2" />
            Pay Now
          </>
        )}
      </Button>
    </form>
  );
};

// Product card component - moved outside to avoid re-creating on every render
// Helper to compute price range for variable products
const getProductPriceDisplay = (product) => {
  if (product.has_variants && product.variants?.length > 0) {
    const prices = product.variants.map(v => v.price || 0).filter(p => p > 0);
    if (prices.length > 0) {
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      if (minPrice !== maxPrice) {
        return { isRange: true, minPrice, maxPrice };
      }
      return { isRange: false, price: minPrice };
    }
  }
  return { isRange: false, price: product.price || 0 };
};

const ProductCard = ({ product, onSelect }) => {
  const priceInfo = getProductPriceDisplay(product);
  
  return (
    <Card 
      className="bg-slate-800/50 border-slate-700 hover:border-orange-500/50 transition-all cursor-pointer group overflow-hidden"
      onClick={() => onSelect(product)}
      data-testid={`product-card-${product.id}`}
    >
      <div className="aspect-square bg-slate-700 relative overflow-hidden">
        {product.images?.[0]?.url ? (
          <img 
            src={product.images[0].url} 
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="h-12 w-12 text-slate-500" />
          </div>
        )}
        {product.compare_at_price && (
          <Badge className="absolute top-2 left-2 bg-red-600">Sale</Badge>
        )}
        {product.product_type === 'digital' && (
          <Badge className="absolute top-2 right-2 bg-purple-600">Digital</Badge>
        )}
      </div>
      <CardContent className="p-4">
        <h3 className="font-medium text-white truncate group-hover:text-orange-400 transition-colors">
          {product.name}
        </h3>
        <div className="flex items-center justify-between mt-2">
          <div>
            {priceInfo.isRange ? (
              <span className="text-lg font-bold text-green-400">
                ${priceInfo.minPrice?.toFixed(2)} - ${priceInfo.maxPrice?.toFixed(2)}
              </span>
            ) : (
              <>
                <span className="text-lg font-bold text-green-400">${priceInfo.price?.toFixed(2)}</span>
                {product.compare_at_price && (
                  <span className="text-sm text-slate-400 line-through ml-2">
                    ${product.compare_at_price?.toFixed(2)}
                  </span>
                )}
              </>
            )}
          </div>
          {product.has_variants && (
            <Badge variant="outline" className="border-slate-600 text-slate-400">
              {product.variants?.length} options
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const ShopPage = () => {
  // Products state
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  // Cart state
  const [cart, setCart] = useState(getCart());
  const [showCart, setShowCart] = useState(false);
  
  // Auth state
  const [customer, setCustomer] = useState(null);
  const [authToken, setAuthToken] = useState(localStorage.getItem('store_auth_token') || '');
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authLoading, setAuthLoading] = useState(false);
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  
  // Checkout state
  const [checkoutStep, setCheckoutStep] = useState(0); // 0: cart, 1: shipping, 2: payment, 3: confirm
  const [shippingMethods, setShippingMethods] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState('calculated');
  const [shippingAddress, setShippingAddress] = useState({
    first_name: '',
    last_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: ''
  });
  const [orderCalculation, setOrderCalculation] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [orderComplete, setOrderComplete] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Account management state
  const [showAccount, setShowAccount] = useState(false);
  const [myOrders, setMyOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
  const [newAddress, setNewAddress] = useState({
    first_name: '',
    last_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: '',
    address_type: 'shipping'
  });
  const [savingAddress, setSavingAddress] = useState(false);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', '12');
      params.append('status', 'active');
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('category_id', selectedCategory);
      
      const response = await axios.get(`${API}/api/store/products?${params.toString()}`);
      setProducts(response.data.products || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, selectedCategory]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/store/categories?include_empty=false`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  // Verify session
  const verifySession = useCallback(async () => {
    if (!authToken) return;
    try {
      const response = await axios.get(`${API}/api/store/customers/me?token=${authToken}`);
      setCustomer(response.data.customer);
    } catch (error) {
      // Session expired or invalid - clear all auth state and notify user
      console.log('Session invalid, clearing auth state');
      localStorage.removeItem('store_auth_token');
      localStorage.removeItem('store_customer');
      setAuthToken(null);
      setCustomer(null);
      toast.info('Your session has expired. Please log in again.');
    }
  }, [authToken]);

  // Fetch shipping methods
  const fetchShippingMethods = useCallback(async () => {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const weight = cart.reduce((sum, item) => sum + ((item.weight || 0) * item.quantity), 0);
    
    try {
      const response = await axios.get(`${API}/api/store/shipping/methods?subtotal=${subtotal}&weight=${weight}`);
      setShippingMethods(response.data.methods || []);
    } catch (error) {
      console.error('Failed to fetch shipping methods:', error);
    }
  }, [cart]);

  // Fetch my orders (for account page)
  const fetchMyOrders = useCallback(async () => {
    if (!authToken) return;
    
    setLoadingOrders(true);
    try {
      const response = await axios.get(`${API}/api/store/customers/orders?token=${authToken}`);
      setMyOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  }, [authToken]);

  // Save new address
  const handleSaveAddress = async (e) => {
    e.preventDefault();
    if (!authToken) return;
    
    setSavingAddress(true);
    try {
      const response = await axios.post(
        `${API}/api/store/customers/addresses?token=${authToken}`,
        newAddress
      );
      
      // Update customer data
      setCustomer({
        ...customer,
        addresses: [...(customer.addresses || []), response.data.address]
      });
      
      setNewAddress({
        first_name: '',
        last_name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US',
        phone: '',
        address_type: 'shipping'
      });
      
      toast.success('Address saved');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save address'));
    } finally {
      setSavingAddress(false);
    }
  };

  // Initial load
  useEffect(() => {
    // Clean up any old localStorage keys from previous versions
    localStorage.removeItem('shop_token');
    localStorage.removeItem('store_token');
    
    fetchProducts();
    fetchCategories();
    verifySession();
  }, [fetchProducts, fetchCategories, verifySession]);

  // Update cart in localStorage
  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  // Handle auth
  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    
    try {
      if (authMode === 'login') {
        const response = await axios.post(`${API}/api/store/customers/login`, {
          email: authForm.email,
          password: authForm.password
        });
        localStorage.setItem('store_auth_token', response.data.token);
        setAuthToken(response.data.token);
        setCustomer(response.data.customer);
        toast.success('Welcome back!');
        setShowAuth(false);
      } else {
        await axios.post(`${API}/api/store/customers/register`, {
          email: authForm.email,
          password: authForm.password,
          first_name: authForm.first_name,
          last_name: authForm.last_name,
          phone: authForm.phone
        });
        toast.success('Account created! Please login.');
        setAuthMode('login');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Authentication failed'));
    } finally {
      setAuthLoading(false);
    }
  };

  // Logout
  const handleLogout = async () => {
    if (authToken) {
      try {
        await axios.post(`${API}/api/store/customers/logout?token=${authToken}`);
      } catch (e) {
        // Ignore
      }
    }
    localStorage.removeItem('store_auth_token');
    localStorage.removeItem('store_customer');
    setAuthToken('');
    setCustomer(null);
    toast.success('Logged out');
  };

  // Add to cart
  const addToCart = (product, variant = null, quantity = 1) => {
    const cartItem = {
      product_id: product.id,
      product_name: product.name,
      variant_id: variant?.id || null,
      variant_name: variant?.name || null,
      price: variant?.price || product.price,
      quantity,
      image_url: product.images?.[0]?.url || null,
      product_type: product.product_type,
      weight: product.weight || 0
    };
    
    // Check if already in cart
    const existingIndex = cart.findIndex(
      item => item.product_id === cartItem.product_id && item.variant_id === cartItem.variant_id
    );
    
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += quantity;
      setCart(newCart);
    } else {
      setCart([...cart, cartItem]);
    }
    
    toast.success(`${product.name} added to cart`);
    setSelectedProduct(null);
  };

  // Update cart quantity
  const updateCartQuantity = (index, quantity) => {
    if (quantity <= 0) {
      removeFromCart(index);
      return;
    }
    const newCart = [...cart];
    newCart[index].quantity = quantity;
    setCart(newCart);
  };

  // Remove from cart
  const removeFromCart = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
  };

  // Calculate cart totals
  const cartTotals = useMemo(() => {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    return { subtotal, itemCount };
  }, [cart]);

  // Start checkout
  const startCheckout = () => {
    if (!customer) {
      setShowAuth(true);
      return;
    }
    fetchShippingMethods();
    setCheckoutStep(1);
    setShowCart(false);
  };

  // Calculate order
  const calculateOrder = async () => {
    try {
      const items = cart.map(item => ({
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity
      }));
      
      const response = await axios.post(`${API}/api/store/checkout/calculate`, items, {
        params: { shipping_method: selectedShipping }
      });
      
      setOrderCalculation(response.data.calculation);
    } catch (error) {
      toast.error('Failed to calculate order');
    }
  };

  // Create payment intent
  const createPaymentIntent = async () => {
    if (!authToken) {
      toast.error('Please login to checkout');
      return;
    }
    
    try {
      const items = cart.map(item => ({
        product_id: item.product_id,
        variant_id: item.variant_id || null,
        quantity: item.quantity || 1
      }));
      
      console.log('Creating payment intent with items:', items);
      console.log('Token:', authToken?.substring(0, 20) + '...');
      console.log('Shipping method:', selectedShipping);
      
      const response = await axios.post(
        `${API}/api/store/checkout/create-payment-intent`,
        items,
        { params: { shipping_method: selectedShipping, token: authToken } }
      );
      
      console.log('Payment intent created:', response.data);
      setClientSecret(response.data.client_secret);
      setPaymentIntentId(response.data.payment_intent_id);
      setOrderCalculation(response.data.calculation);
      setCheckoutStep(2);
    } catch (error) {
      console.error('Payment intent error:', error.response?.data);
      // Handle session expiry - force re-login
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('store_auth_token');
        localStorage.removeItem('store_customer');
        setAuthToken(null);
        setCustomer(null);
        setShowAuth(true);
        setCheckoutStep(0);
        return;
      }
      toast.error(getErrorMessage(error, 'Failed to create payment'));
    }
  };

  // Complete order
  const completeOrder = async (paymentIntentId) => {
    try {
      // Convert cart items to the format expected by backend
      const cartItems = cart.map(item => ({
        product_id: item.product_id,
        variant_id: item.variant_id || null,
        quantity: item.quantity || 1
      }));
      
      console.log('Completing order with:', { paymentIntentId, shippingAddress, selectedShipping, cartItems });
      
      const response = await axios.post(
        `${API}/api/store/checkout/complete`,
        {
          shipping_address: shippingAddress,
          billing_address: null,
          items: cartItems
        },
        {
          params: {
            payment_intent_id: paymentIntentId,
            shipping_method: selectedShipping,
            token: authToken,
            notes: ''
          }
        }
      );
      
      console.log('Order complete:', response.data);
      setOrderComplete(response.data.order);
      setCheckoutStep(3);
      setCart([]);
      localStorage.removeItem('shop_cart');
      toast.success('Order placed successfully!');
    } catch (error) {
      console.error('Complete order error:', error.response?.data);
      toast.error(getErrorMessage(error, 'Failed to complete order'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingBag className="h-8 w-8 text-orange-500" />
              <div>
                <h1 className="text-xl font-bold text-white">Biggest Little CPR</h1>
                <p className="text-xs text-slate-400">Training Equipment & Supplies</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Back to Classes button */}
              <Button
                onClick={() => window.location.href = '/'}
                variant="outline"
                size="sm"
                className="border-teal-600 text-teal-400 hover:bg-teal-900/30"
                data-testid="back-to-classes-button"
              >
                <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
                Book a Class
              </Button>
              
              {/* Search */}
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64 bg-slate-800 border-slate-600 text-white"
                />
              </div>
              
              {/* Account */}
              {customer ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-300 hidden md:block">
                    Hi, {customer.first_name}
                  </span>
                  <Button
                    onClick={() => { setShowAccount(true); fetchMyOrders(); }}
                    variant="ghost"
                    size="sm"
                    className="text-purple-400 hover:text-purple-300 hover:bg-purple-900/30"
                    data-testid="my-account-button"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowAuth(true)}
                  variant="outline"
                  size="sm"
                  className="border-slate-600 text-white hover:bg-slate-700"
                  data-testid="login-button"
                >
                  <User className="h-4 w-4 mr-2" />
                  Login
                </Button>
              )}
              
              {/* Cart */}
              <Button
                onClick={() => setShowCart(true)}
                variant="outline"
                className="relative border-orange-600 text-orange-400 hover:bg-orange-600 hover:text-white"
                data-testid="cart-button"
              >
                <ShoppingCart className="h-5 w-5" />
                {cartTotals.itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-orange-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cartTotals.itemCount}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Sidebar - Categories */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <Card className="bg-slate-800/50 border-slate-700 sticky top-24 max-h-[calc(100vh-120px)] overflow-hidden flex flex-col">
              <CardHeader className="pb-3 flex-shrink-0">
                <CardTitle className="text-white flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  Categories
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 overflow-y-auto flex-1">
                <div className="space-y-1">
                  <Button
                    onClick={() => setSelectedCategory('')}
                    variant={selectedCategory === '' ? 'secondary' : 'ghost'}
                    className={`w-full justify-start ${selectedCategory === '' ? 'bg-orange-600 text-white' : 'text-slate-300 hover:text-white'}`}
                  >
                    All Products
                  </Button>
                  {/* Show parent categories with their children */}
                  {categories
                    .filter(cat => !cat.parent_id) // Only parent categories
                    .map((parentCat) => {
                      const childCats = categories.filter(c => c.parent_id === parentCat.id);
                      const isParentSelected = selectedCategory === parentCat.id;
                      const isChildSelected = childCats.some(c => c.id === selectedCategory);
                      
                      return (
                        <div key={parentCat.id}>
                          <Button
                            onClick={() => setSelectedCategory(parentCat.id)}
                            variant={isParentSelected ? 'secondary' : 'ghost'}
                            className={`w-full justify-start text-left ${isParentSelected ? 'bg-orange-600 text-white' : 'text-slate-300 hover:text-white'}`}
                          >
                            <span className="truncate">{parentCat.name.replace(/&amp;/g, '&')}</span>
                            <Badge variant="outline" className="ml-auto border-slate-600 text-slate-400 text-xs">
                              {parentCat.product_count || 0}
                            </Badge>
                          </Button>
                          {/* Show children if parent is selected or a child is selected */}
                          {(isParentSelected || isChildSelected || childCats.length > 0) && childCats.length > 0 && (
                            <div className="ml-3 pl-2 border-l border-slate-700 mt-1 mb-2 space-y-1">
                              {childCats.map((childCat) => (
                                <Button
                                  key={childCat.id}
                                  onClick={() => setSelectedCategory(childCat.id)}
                                  variant={selectedCategory === childCat.id ? 'secondary' : 'ghost'}
                                  size="sm"
                                  className={`w-full justify-start text-left text-sm ${selectedCategory === childCat.id ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'}`}
                                >
                                  <span className="truncate">{childCat.name.replace(/&amp;/g, '&')}</span>
                                  <Badge variant="outline" className="ml-auto border-slate-600 text-slate-500 text-xs">
                                    {childCat.product_count || 0}
                                  </Badge>
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Mobile Search */}
            <div className="lg:hidden mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-600 text-white"
                />
              </div>
            </div>

            {/* Mobile Category Filter */}
            <div className="lg:hidden mb-4">
              <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Products */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-12 w-12 animate-spin text-orange-500" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <Package className="h-16 w-16 mx-auto text-slate-500 mb-4" />
                <h3 className="text-xl font-medium text-white mb-2">No products found</h3>
                <p className="text-slate-400">Try adjusting your search or filters</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} onSelect={setSelectedProduct} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-4 mt-8">
                    <Button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      variant="outline"
                      className="border-slate-600"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-slate-400">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      variant="outline"
                      className="border-slate-600"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Product Detail Modal */}
      <Dialog open={!!selectedProduct} onOpenChange={() => { setSelectedProduct(null); setSelectedVariant(null); }}>
        <DialogContent className="max-w-3xl bg-slate-900 border-slate-700 text-white">
          {selectedProduct && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Image */}
              <div className="aspect-square bg-slate-800 rounded-lg overflow-hidden">
                {selectedProduct.images?.[0]?.url ? (
                  <img 
                    src={selectedProduct.images[0].url} 
                    alt={selectedProduct.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="h-20 w-20 text-slate-500" />
                  </div>
                )}
              </div>

              {/* Details */}
              <div>
                <DialogHeader>
                  <DialogTitle className="text-xl text-white">{selectedProduct.name}</DialogTitle>
                  {selectedProduct.sku && (
                    <p className="text-sm text-slate-400">SKU: {selectedProduct.sku}</p>
                  )}
                </DialogHeader>

                <div className="mt-4">
                  <div className="text-2xl font-bold text-green-400">
                    ${(selectedVariant?.price || selectedProduct.price)?.toFixed(2)}
                    {selectedProduct.compare_at_price && (
                      <span className="text-lg text-slate-400 line-through ml-2">
                        ${selectedProduct.compare_at_price?.toFixed(2)}
                      </span>
                    )}
                  </div>

                  {/* Variants */}
                  {selectedProduct.has_variants && selectedProduct.variants?.length > 0 && (
                    <div className="mt-4">
                      <Label className="text-slate-300">Options</Label>
                      <Select 
                        value={selectedVariant?.id || ''} 
                        onValueChange={(v) => setSelectedVariant(selectedProduct.variants.find(x => x.id === v))}
                      >
                        <SelectTrigger className="mt-1 bg-slate-800 border-slate-600 text-white">
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600">
                          {selectedProduct.variants.map((v) => (
                            <SelectItem key={v.id} value={v.id}>
                              {v.name} - ${v.price?.toFixed(2)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Description */}
                  {selectedProduct.short_description && (
                    <p className="mt-4 text-slate-300 text-sm">
                      {selectedProduct.short_description.replace(/<[^>]*>/g, '')}
                    </p>
                  )}

                  {/* Badges */}
                  <div className="flex gap-2 mt-4">
                    <Badge className={selectedProduct.product_type === 'digital' ? 'bg-purple-600' : 'bg-blue-600'}>
                      {selectedProduct.product_type}
                    </Badge>
                    {selectedProduct.stock_status === 'instock' ? (
                      <Badge className="bg-green-600">In Stock</Badge>
                    ) : (
                      <Badge className="bg-red-600">Out of Stock</Badge>
                    )}
                  </div>

                  {/* Add to Cart */}
                  <Button
                    onClick={() => addToCart(selectedProduct, selectedVariant)}
                    disabled={selectedProduct.has_variants && !selectedVariant}
                    className="w-full mt-6 bg-orange-600 hover:bg-orange-700"
                    data-testid="add-to-cart-button"
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Add to Cart
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cart Drawer */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-orange-400" />
              Shopping Cart ({cartTotals.itemCount} items)
            </DialogTitle>
          </DialogHeader>

          {cart.length === 0 ? (
            <div className="py-8 text-center">
              <ShoppingCart className="h-12 w-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">Your cart is empty</p>
            </div>
          ) : (
            <>
              <ScrollArea className="max-h-[400px]">
                <div className="space-y-4">
                  {cart.map((item, index) => (
                    <div key={index} className="flex gap-4 p-3 bg-slate-800/50 rounded-lg">
                      <div className="w-16 h-16 bg-slate-700 rounded overflow-hidden flex-shrink-0">
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="h-6 w-6 text-slate-500" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-white truncate">{item.product_name}</h4>
                        {item.variant_name && (
                          <p className="text-xs text-slate-400">{item.variant_name}</p>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => updateCartQuantity(index, item.quantity - 1)}
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-slate-400 hover:text-white"
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="text-white w-6 text-center">{item.quantity}</span>
                            <Button
                              onClick={() => updateCartQuantity(index, item.quantity + 1)}
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-slate-400 hover:text-white"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                          <span className="font-medium text-green-400">
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <Button
                        onClick={() => removeFromCart(index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              <Separator className="my-4 bg-slate-700" />

              <div className="space-y-2">
                <div className="flex justify-between text-lg font-medium">
                  <span className="text-slate-300">Subtotal</span>
                  <span className="text-white">${cartTotals.subtotal.toFixed(2)}</span>
                </div>
                <p className="text-xs text-slate-400">Shipping and taxes calculated at checkout</p>
              </div>

              <Button
                onClick={startCheckout}
                className="w-full bg-orange-600 hover:bg-orange-700 mt-4"
                data-testid="checkout-button"
              >
                Proceed to Checkout
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Auth Modal */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="max-w-sm bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">
              {authMode === 'login' ? 'Sign In' : 'Create Account'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {authMode === 'login' 
                ? 'Sign in to your account to checkout'
                : 'Create an account to start shopping'
              }
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAuth} className="space-y-4">
            {authMode === 'register' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">First Name</Label>
                  <Input
                    value={authForm.first_name}
                    data-testid="auth-first-name-input"
                    onChange={(e) => setAuthForm({...authForm, first_name: e.target.value})}
                    className="mt-1 bg-slate-800 border-slate-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Last Name</Label>
                  <Input
                    value={authForm.last_name}
                    data-testid="auth-last-name-input"
                    onChange={(e) => setAuthForm({...authForm, last_name: e.target.value})}
                    className="mt-1 bg-slate-800 border-slate-600 text-white"
                    required
                  />
                </div>
              </div>
            )}

            <div>
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={authForm.email}
                data-testid="auth-email-input"
                onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                className="mt-1 bg-slate-800 border-slate-600 text-white"
                required
              />
            </div>

            <div>
              <Label className="text-slate-300">Password</Label>
              <Input
                type="password"
                value={authForm.password}
                data-testid="auth-password-input"
                onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                className="mt-1 bg-slate-800 border-slate-600 text-white"
                required
                minLength={8}
              />
            </div>

            {authMode === 'register' && (
              <div>
                <Label className="text-slate-300">Phone (optional)</Label>
                <Input
                  type="tel"
                  value={authForm.phone}
                  data-testid="auth-phone-input"
                  onChange={(e) => setAuthForm({...authForm, phone: e.target.value})}
                  className="mt-1 bg-slate-800 border-slate-600 text-white"
                />
              </div>
            )}

            <Button type="submit" disabled={authLoading} data-testid="auth-submit-button" className="w-full bg-orange-600 hover:bg-orange-700">
              {authLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : authMode === 'login' ? (
                'Sign In'
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <div className="text-center mt-4">
            <button
              onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
              className="text-sm text-orange-400 hover:underline"
            >
              {authMode === 'login' 
                ? "Don't have an account? Create one"
                : 'Already have an account? Sign in'
              }
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkout Modal */}
      <Dialog open={checkoutStep > 0} onOpenChange={(open) => !open && setCheckoutStep(0)}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-700 text-white">
          {/* Step 1: Shipping */}
          {checkoutStep === 1 && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Truck className="h-5 w-5 text-orange-400" />
                  Shipping Information
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">First Name</Label>
                    <Input
                      value={shippingAddress.first_name}
                      data-testid="ship-first-name-input"
                      onChange={(e) => setShippingAddress({...shippingAddress, first_name: e.target.value})}
                      className="mt-1 bg-slate-800 border-slate-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Last Name</Label>
                    <Input
                      value={shippingAddress.last_name}
                      data-testid="ship-last-name-input"
                      onChange={(e) => setShippingAddress({...shippingAddress, last_name: e.target.value})}
                      className="mt-1 bg-slate-800 border-slate-600 text-white"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Address</Label>
                  <Input
                    value={shippingAddress.address_line1}
                    data-testid="ship-address1-input"
                    onChange={(e) => setShippingAddress({...shippingAddress, address_line1: e.target.value})}
                    className="mt-1 bg-slate-800 border-slate-600 text-white"
                    placeholder="Street address"
                    required
                  />
                </div>

                <div>
                  <Input
                    value={shippingAddress.address_line2}
                    onChange={(e) => setShippingAddress({...shippingAddress, address_line2: e.target.value})}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="Apt, suite, etc. (optional)"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">City</Label>
                    <Input
                      value={shippingAddress.city}
                      data-testid="ship-city-input"
                      onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                      className="mt-1 bg-slate-800 border-slate-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">State</Label>
                    <Input
                      value={shippingAddress.state}
                      data-testid="ship-state-input"
                      onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                      className="mt-1 bg-slate-800 border-slate-600 text-white"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">ZIP Code</Label>
                    <Input
                      value={shippingAddress.postal_code}
                      data-testid="ship-zip-input"
                      onChange={(e) => setShippingAddress({...shippingAddress, postal_code: e.target.value})}
                      className="mt-1 bg-slate-800 border-slate-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Phone</Label>
                    <Input
                      value={shippingAddress.phone}
                      data-testid="ship-phone-input"
                      onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                      className="mt-1 bg-slate-800 border-slate-600 text-white"
                    />
                  </div>
                </div>

                <Separator className="my-4 bg-slate-700" />

                <div>
                  <Label className="text-slate-300">Shipping Method</Label>
                  <div className="mt-2 space-y-2">
                    {shippingMethods.filter(m => m.available).map((method) => (
                      <div
                        key={method.id}
                        data-testid={`shipping-method-${method.id}`}
                        onClick={() => setSelectedShipping(method.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedShipping === method.id 
                            ? 'border-orange-500 bg-orange-500/10' 
                            : 'border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        <div className="flex justify-between">
                          <span className="font-medium text-white">{method.name}</span>
                          <span className="text-green-400">
                            {method.price === 0 ? 'Free' : `$${method.price.toFixed(2)}`}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{method.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Terms and Conditions */}
                <div className="flex items-start gap-3 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <Checkbox 
                    id="terms" 
                    checked={acceptedTerms}
                    onCheckedChange={(checked) => setAcceptedTerms(checked)}
                    className="mt-0.5 border-slate-500 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600"
                  />
                  <div className="flex-1">
                    <label 
                      htmlFor="terms" 
                      className="text-sm text-slate-300 cursor-pointer leading-relaxed"
                    >
                      I agree to the{' '}
                      <a 
                        href="/terms" 
                        target="_blank" 
                        className="text-orange-400 hover:text-orange-300 underline"
                      >
                        Terms and Conditions
                      </a>
                      {' '}and{' '}
                      <a 
                        href="/privacy" 
                        target="_blank" 
                        className="text-orange-400 hover:text-orange-300 underline"
                      >
                        Privacy Policy
                      </a>
                      . I understand that all sales are final and acknowledge the refund policy.
                    </label>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setCheckoutStep(0)} className="border-slate-600">
                  Back to Cart
                </Button>
                <Button 
                  onClick={createPaymentIntent} 
                  data-testid="continue-to-payment-button" 
                  className="bg-orange-600 hover:bg-orange-700"
                  disabled={!acceptedTerms}
                >
                  Continue to Payment
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Step 2: Payment */}
          {checkoutStep === 2 && clientSecret && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-orange-400" />
                  Payment
                </DialogTitle>
              </DialogHeader>

              {/* Order Summary */}
              {orderCalculation && (
                <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-white mb-2">Order Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between text-slate-300">
                      <span>Subtotal</span>
                      <span>${orderCalculation.subtotal?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-slate-300">
                      <span>Shipping</span>
                      <span>${orderCalculation.shipping_cost?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-slate-300">
                      <span>Tax</span>
                      <span>${orderCalculation.tax?.toFixed(2)}</span>
                    </div>
                    <Separator className="my-2 bg-slate-600" />
                    <div className="flex justify-between font-medium text-white">
                      <span>Total</span>
                      <span className="text-green-400">${orderCalculation.total?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <CheckoutPaymentForm
                  clientSecret={clientSecret}
                  onSuccess={(piId) => completeOrder(piId)}
                  onError={(err) => toast.error(typeof err === 'string' ? err : 'Payment failed. Please try again.')}
                  processing={processingPayment}
                  setProcessing={setProcessingPayment}
                />
              </Elements>

              <Button 
                variant="outline" 
                onClick={() => setCheckoutStep(1)} 
                className="w-full mt-4 border-slate-600"
                disabled={processingPayment}
              >
                Back to Shipping
              </Button>
            </>
          )}

          {/* Step 3: Confirmation */}
          {checkoutStep === 3 && orderComplete && (
            <>
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-10 w-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Order Confirmed!</h2>
                <p className="text-slate-400 mb-4">
                  Thank you for your order. We&apos;ve sent a confirmation to your email.
                </p>
                <div className="bg-slate-800/50 rounded-lg p-4 inline-block">
                  <p className="text-sm text-slate-400">Order Number</p>
                  <p className="text-xl font-mono font-bold text-orange-400">
                    {orderComplete.order_number}
                  </p>
                </div>
              </div>

              <Button
                onClick={() => { setCheckoutStep(0); setShowCart(false); }}
                className="w-full bg-orange-600 hover:bg-orange-700"
              >
                Continue Shopping
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* My Account Dialog */}
      <Dialog open={showAccount} onOpenChange={setShowAccount}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-white max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <User className="h-5 w-5 text-purple-400" />
              My Account
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Manage your addresses and view order history
            </DialogDescription>
          </DialogHeader>
          
          {customer && (
            <Tabs defaultValue="orders" className="flex-1 min-h-0 flex flex-col">
              <TabsList className="grid w-full grid-cols-2 bg-slate-800/50 flex-shrink-0">
                <TabsTrigger value="orders" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                  <History className="h-4 w-4 mr-2" />
                  Order History
                </TabsTrigger>
                <TabsTrigger value="addresses" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                  <MapPin className="h-4 w-4 mr-2" />
                  Addresses
                </TabsTrigger>
              </TabsList>
              
              {/* Orders Tab */}
              <TabsContent value="orders" className="flex-1 overflow-auto mt-4">
                {loadingOrders ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                  </div>
                ) : selectedOrderDetail ? (
                  /* Order Detail View */
                  <div className="space-y-4" data-testid="order-detail-view">
                    <Button
                      onClick={() => setSelectedOrderDetail(null)}
                      variant="ghost"
                      className="text-slate-400 hover:text-white mb-2"
                      data-testid="back-to-orders-btn"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Back to Orders
                    </Button>
                    
                    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700" data-testid="order-detail-card">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-white" data-testid="order-detail-number">
                            Order #{selectedOrderDetail.order_number}
                          </h3>
                          <p className="text-sm text-slate-400">
                            {new Date(selectedOrderDetail.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <Badge className={
                          selectedOrderDetail.fulfillment_status === 'complete' ? 'bg-green-600' :
                          selectedOrderDetail.fulfillment_status === 'shipped' ? 'bg-purple-600' :
                          selectedOrderDetail.fulfillment_status === 'filling' ? 'bg-yellow-600' :
                          selectedOrderDetail.status === 'processing' ? 'bg-blue-600' :
                          'bg-slate-600'
                        }>
                          {selectedOrderDetail.fulfillment_status || selectedOrderDetail.status}
                        </Badge>
                      </div>
                      
                      {/* Order Items */}
                      <div className="border-t border-slate-700 pt-4">
                        <h4 className="font-medium text-white mb-3">Items</h4>
                        {selectedOrderDetail.items?.length > 0 ? (
                          <div className="space-y-3">
                            {selectedOrderDetail.items.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-3 bg-slate-900/50 p-3 rounded-lg">
                                <div className="w-12 h-12 bg-slate-700 rounded overflow-hidden flex-shrink-0">
                                  {item.image_url ? (
                                    <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Package className="h-5 w-5 text-slate-500" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-white truncate">{item.product_name}</p>
                                  {item.variant_name && (
                                    <p className="text-xs text-slate-400">{item.variant_name}</p>
                                  )}
                                  <p className="text-xs text-slate-500">Qty: {item.quantity}</p>
                                </div>
                                <div className="text-right">
                                  <span className="font-medium text-green-400">
                                    ${(item.line_total || item.price * item.quantity)?.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-500 text-sm">No item details available</p>
                        )}
                      </div>
                      
                      {/* Order Totals */}
                      <div className="border-t border-slate-700 pt-4 mt-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Subtotal</span>
                          <span className="text-white">${(selectedOrderDetail.subtotal || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Shipping</span>
                          <span className="text-white">${(selectedOrderDetail.shipping_cost || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Tax</span>
                          <span className="text-white">${(selectedOrderDetail.tax || 0).toFixed(2)}</span>
                        </div>
                        <Separator className="my-2 bg-slate-700" />
                        <div className="flex justify-between font-medium">
                          <span className="text-white">Total</span>
                          <span className="text-green-400 text-lg">${(selectedOrderDetail.total || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      
                      {/* Shipping Address */}
                      {selectedOrderDetail.shipping_address && (
                        <div className="border-t border-slate-700 pt-4 mt-4">
                          <h4 className="font-medium text-white mb-2">Shipping Address</h4>
                          <div className="text-sm text-slate-400">
                            <p>{selectedOrderDetail.shipping_address.first_name} {selectedOrderDetail.shipping_address.last_name}</p>
                            <p>{selectedOrderDetail.shipping_address.address_line1 || selectedOrderDetail.shipping_address.address_1}</p>
                            {(selectedOrderDetail.shipping_address.address_line2 || selectedOrderDetail.shipping_address.address_2) && (
                              <p>{selectedOrderDetail.shipping_address.address_line2 || selectedOrderDetail.shipping_address.address_2}</p>
                            )}
                            <p>
                              {selectedOrderDetail.shipping_address.city}, {selectedOrderDetail.shipping_address.state} {selectedOrderDetail.shipping_address.postal_code || selectedOrderDetail.shipping_address.postcode}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Tracking Info */}
                      {selectedOrderDetail.tracking_number && (
                        <div className="border-t border-slate-700 pt-4 mt-4" data-testid="order-tracking-info">
                          <h4 className="font-medium text-white mb-2">Tracking</h4>
                          <p className="text-sm text-purple-400 font-mono">{selectedOrderDetail.tracking_number}</p>
                          {selectedOrderDetail.tracking_carrier && (
                            <p className="text-xs text-slate-400 mt-1">Carrier: {selectedOrderDetail.tracking_carrier}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : myOrders.length === 0 ? (
                  <div className="text-center py-8 text-slate-400" data-testid="no-orders-message">
                    <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No orders yet</p>
                    <p className="text-sm mt-1">Your purchase history will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myOrders.map((order) => (
                      <Card 
                        key={order.id} 
                        className="bg-slate-800/50 border-slate-700 hover:border-purple-500/50 cursor-pointer transition-colors"
                        onClick={() => setSelectedOrderDetail(order)}
                        data-testid={`customer-order-${order.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-bold text-orange-400">
                                  {order.order_number}
                                </span>
                                <Badge className={
                                  order.fulfillment_status === 'complete' ? 'bg-green-600' :
                                  order.fulfillment_status === 'shipped' ? 'bg-purple-600' :
                                  order.status === 'processing' ? 'bg-blue-600' :
                                  'bg-slate-600'
                                }>
                                  {order.fulfillment_status || order.status}
                                </Badge>
                              </div>
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(order.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                              {/* Show item preview */}
                              {order.items?.length > 0 && (
                                <p className="text-xs text-slate-500 mt-2 truncate max-w-[200px]">
                                  {order.items.map(i => i.product_name).join(', ')}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="font-bold text-green-400">
                                ${order.total?.toFixed(2)}
                              </span>
                              <p className="text-xs text-slate-400">
                                {order.items?.length || 0} items
                              </p>
                              <p className="text-xs text-purple-400 mt-1">
                                View Details →
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
              
              {/* Addresses Tab */}
              <TabsContent value="addresses" className="flex-1 overflow-auto mt-4">
                {/* Existing Addresses */}
                {customer.addresses?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-slate-300 mb-3">Saved Addresses</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {customer.addresses.map((addr) => (
                        <Card key={addr.id} className="bg-slate-800/50 border-slate-700">
                          <CardContent className="p-3">
                            <div className="text-sm">
                              <p className="font-medium text-white">
                                {addr.first_name} {addr.last_name}
                              </p>
                              <p className="text-slate-400">{addr.address_line1}</p>
                              {addr.address_line2 && <p className="text-slate-400">{addr.address_line2}</p>}
                              <p className="text-slate-400">
                                {addr.city}, {addr.state} {addr.postal_code}
                              </p>
                              {addr.phone && <p className="text-slate-500 text-xs mt-1">{addr.phone}</p>}
                            </div>
                            {addr.is_default && (
                              <Badge className="bg-purple-600 text-xs mt-2">Default</Badge>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Add New Address */}
                <div className="border-t border-slate-700 pt-4">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Add New Address</h4>
                  <form onSubmit={handleSaveAddress} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">First Name</Label>
                        <Input
                          value={newAddress.first_name}
                          onChange={(e) => setNewAddress({...newAddress, first_name: e.target.value})}
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Last Name</Label>
                        <Input
                          value={newAddress.last_name}
                          onChange={(e) => setNewAddress({...newAddress, last_name: e.target.value})}
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300">Address</Label>
                      <Input
                        value={newAddress.address_line1}
                        onChange={(e) => setNewAddress({...newAddress, address_line1: e.target.value})}
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                        placeholder="Street address"
                        required
                      />
                    </div>
                    <div>
                      <Input
                        value={newAddress.address_line2}
                        onChange={(e) => setNewAddress({...newAddress, address_line2: e.target.value})}
                        className="bg-slate-800 border-slate-600 text-white"
                        placeholder="Apt, suite, etc. (optional)"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-slate-300">City</Label>
                        <Input
                          value={newAddress.city}
                          onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">State</Label>
                        <Input
                          value={newAddress.state}
                          onChange={(e) => setNewAddress({...newAddress, state: e.target.value})}
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">ZIP</Label>
                        <Input
                          value={newAddress.postal_code}
                          onChange={(e) => setNewAddress({...newAddress, postal_code: e.target.value})}
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300">Phone</Label>
                      <Input
                        value={newAddress.phone}
                        onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})}
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <Button type="submit" disabled={savingAddress} className="bg-purple-600 hover:bg-purple-700">
                      {savingAddress ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Save Address
                    </Button>
                  </form>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShopPage;
